<template>
  <div class="page-shell">
    <section class="container auth-container auth-container--wide">
      <div class="page-intro">
        <p class="eyebrow">Member Register</p>
        <h1 class="page-title">注册网站会员</h1>
      </div>

      <form class="panel auth-panel" @submit.prevent="handleSubmit">
        <transition name="toast-fade">
          <div v-if="toast.visible" :class="['register-toast', `register-toast--${toast.type}`]">
            {{ toast.message }}
          </div>
        </transition>

        <div class="form-grid">
          <label class="form-field">
            <span class="form-label">单位名称</span>
            <input v-model.trim="form.unit_name" type="text" placeholder="请输入单位名称">
          </label>

          <label class="form-field">
            <span class="form-label">用户名</span>
            <input v-model.trim="form.username" type="text" placeholder="请输入用户名">
          </label>

          <label class="form-field">
            <span class="form-label">真实姓名</span>
            <input v-model.trim="form.real_name" type="text" placeholder="请输入真实姓名">
          </label>

          <label class="form-field">
            <span class="form-label">证件号码</span>
            <input v-model.trim="form.certificate_no" type="text" placeholder="请输入证件号码">
          </label>

          <label class="form-field">
            <span class="form-label">手机号</span>
            <input v-model.trim="form.mobile" type="tel" placeholder="请输入手机号">
          </label>

          <label class="form-field">
            <span class="form-label">微信号</span>
            <input v-model.trim="form.wechat_id" type="text" placeholder="请输入微信号">
          </label>

          <label class="form-field">
            <span class="form-label">短信验证码</span>
            <div class="inline-field">
              <input v-model.trim="form.code" type="text" placeholder="请输入验证码">
              <button
                class="btn btn--secondary inline-field__button"
                type="button"
                :disabled="sendingCode || countdown > 0"
                @click="handleSendCode"
              >
                {{ countdown > 0 ? `${countdown}s` : (sendingCode ? '发送中...' : '发送验证码') }}
              </button>
            </div>
          </label>

          <label class="form-field">
            <span class="form-label">密码</span>
            <input v-model.trim="form.password" type="password" placeholder="请输入密码">
          </label>

          <label class="form-field">
            <span class="form-label">确认密码</span>
            <input
              v-model.trim="form.password_confirm"
              type="password"
              placeholder="请再次输入密码"
            >
          </label>
        </div>

        <div class="auth-actions">
          <button class="btn btn--primary" type="submit" :disabled="submitting">
            {{ submitting ? '注册中...' : '完成注册' }}
          </button>
          <router-link class="btn btn--ghost" :to="{ name: 'login' }">返回登录</router-link>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import { registerMember, sendAuthCode } from '@/api/auth'
import { applyAuthSession, hydrateSession } from '@/store/session'

export default {
  name: 'RegisterView',
  data() {
    return {
      submitting: false,
      sendingCode: false,
      countdown: 0,
      timerId: null,
      toastTimerId: null,
      toast: {
        visible: false,
        message: '',
        type: 'error'
      },
      form: {
        unit_name: '',
        username: '',
        real_name: '',
        certificate_no: '',
        mobile: '',
        wechat_id: '',
        code: '',
        password: '',
        password_confirm: ''
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown()
    this.clearToastTimer()
  },
  methods: {
    validateForm() {
      const requiredFields = [
        ['unit_name', '单位名称'],
        ['username', '用户名'],
        ['real_name', '真实姓名'],
        ['certificate_no', '证件号码'],
        ['mobile', '手机号'],
        ['wechat_id', '微信号'],
        ['code', '短信验证码'],
        ['password', '密码'],
        ['password_confirm', '确认密码']
      ]
      const missingField = requiredFields.find(([field]) => !this.form[field])

      if (missingField) {
        return `请填写${missingField[1]}。`
      }

      if (this.form.password !== this.form.password_confirm) {
        return '两次输入的密码不一致。'
      }

      return ''
    },
    startCountdown(seconds) {
      this.stopCountdown()
      this.countdown = seconds

      this.timerId = window.setInterval(() => {
        if (this.countdown <= 1) {
          this.stopCountdown()
          return
        }

        this.countdown -= 1
      }, 1000)
    },
    stopCountdown() {
      if (this.timerId) {
        window.clearInterval(this.timerId)
        this.timerId = null
      }

      this.countdown = 0
    },
    clearToastTimer() {
      if (this.toastTimerId) {
        window.clearTimeout(this.toastTimerId)
        this.toastTimerId = null
      }
    },
    showToast(message, type = 'error') {
      this.clearToastTimer()
      this.toast = {
        visible: true,
        message,
        type
      }
      this.toastTimerId = window.setTimeout(() => {
        this.toast.visible = false
        this.toastTimerId = null
      }, 2200)
    },
    async handleSendCode() {
      if (!this.form.mobile) {
        this.showToast('请先填写手机号。')
        return
      }

      this.sendingCode = true

      try {
        const response = await sendAuthCode({
          mobile: this.form.mobile,
          scene: 'register'
        })

        this.showToast('验证码已发送，请注意查收。', 'success')
        this.startCountdown(Math.min(Number(response.expire_seconds) || 60, 60))
      } catch (error) {
        this.showToast(error.message || '验证码发送失败')
      } finally {
        this.sendingCode = false
      }
    },
    async handleSubmit() {
      const validationMessage = this.validateForm()

      if (validationMessage) {
        this.showToast(validationMessage)
        return
      }

      this.submitting = true

      try {
        const session = await registerMember({
          ...this.form
        })

        applyAuthSession(session)
        await hydrateSession().catch(() => {})

        this.$router.push({ name: 'profile' })
      } catch (error) {
        this.showToast(error.message || '注册失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.auth-container {
  max-width: 620px;
}

.auth-container--wide {
  max-width: 880px;
}

.auth-panel {
  position: relative;
  display: grid;
  gap: 18px;
  padding: 24px;
}

.register-toast {
  position: absolute;
  top: 18px;
  left: 50%;
  z-index: 2;
  max-width: calc(100% - 48px);
  padding: 10px 16px;
  border-radius: 999px;
  background: #fef2f2;
  color: #b42318;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  transform: translateX(-50%);
  pointer-events: none;
}

.register-toast--success {
  background: #ecfdf3;
  color: #027a48;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.toast-fade-enter,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}

.inline-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
}

.inline-field__button {
  min-width: 132px;
}

.auth-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

@media (max-width: 640px) {
  .auth-panel {
    padding: 20px;
  }

  .inline-field {
    grid-template-columns: 1fr;
  }

  .inline-field__button {
    width: 100%;
  }
}
</style>
